import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ArrowRight, ShieldCheck, Compass, Calendar, Layers, Target,
  Repeat, ChevronRight, Users, Building2, Anchor, Waves, Wrench,
  ClipboardCheck, Zap, LineChart, Sparkles,
} from "lucide-react";

// Verticals covered by DMS
const VERTICALS = [
  { icon: Building2, label: "Dive centres" },
  { icon: Anchor, label: "Liveaboard operators" },
  { icon: Wrench, label: "Gear shops" },
  { icon: Waves, label: "Freediving schools" },
  { icon: Compass, label: "Dive travel operators" },
  { icon: ShieldCheck, label: "Conservation projects" },
];

const WORKFLOW = [
  { n: "01", title: "Onboard the business", body: "Offers, goals, audience, active channels, brand voice, available assets, real-world constraints. 15 to 25 minutes with an owner or marketing lead." },
  { n: "02", title: "Generate the foundation", body: "Positioning, content pillars, channel roles, campaign arcs, weekly themes. Every element built around the actual business — not a template." },
  { n: "03", title: "Refine in plain language", body: "\u201CRemove TikTok. Add advanced wreck content. Make the tone calmer.\u201D The strategy structure updates before you commit." },
  { n: "04", title: "Approve. Generate 90-day calendar.", body: "One task per posting day, mapped across three strategic months, respecting your seasonality, capacity and campaign timing." },
  { n: "05", title: "Work from Today\u2019s Task", body: "Hook, angle, CTA and prep notes included. Mark done. Skip. Or click Generate to produce the full content instantly." },
];

const OUTCOMES = [
  { icon: Target, title: "Positioning built around your business", body: "Not a template. Not a competitor lookalike. The plan reflects your offers, market and season." },
  { icon: Layers, title: "Content pillars and buckets tied to offers", body: "Every pillar exists to promote something real. No filler categories, no random content." },
  { icon: Repeat, title: "Defined roles for every channel you actually use", body: "A focused three-channel strategy beats a chaotic ten-channel fantasy." },
  { icon: Calendar, title: "Campaign arcs that build across three months", body: "Awareness \u2192 objection breaking \u2192 social proof \u2192 conversion \u2192 nurture. The story compounds." },
  { icon: ClipboardCheck, title: "A daily task with hook, angle, CTA and prep notes", body: "Written in your brand voice, aligned to the strategy. Ready to shoot, ready to post." },
  { icon: Zap, title: "One-click content generation", body: "Any daily task becomes a fully generated reel, caption, email, blog, carousel or ad in seconds." },
];

const COMPARISON = [
  ["Content ideas", "Yes", "Yes"],
  ["Brand-aware outputs", "Limited", "Every output tuned to your Dive Brain"],
  ["A rolling 90-day strategy", "\u2014", "Built for your business, refined in your voice"],
  ["One clear task per day", "\u2014", "With hook, angle, CTA and prep notes"],
  ["Multi-workspace (agencies, groups)", "\u2014", "Included"],
  ["Strategy Credits", "Limited", "Unlimited"],
  ["Ideal for", "Solo creators, individuals", "Dive businesses and marketing teams"],
];

const FAQS = [
  { q: "Who runs the Strategy Operator day-to-day?", a: "One person sets up the strategy (owner, GM, or marketing lead). Anyone on the team then works from the daily calendar. If a task feels wrong for today, they skip it. The plan keeps rolling." },
  { q: "How long does the daily task take?", a: "Between 5 and 15 minutes if the asset already exists (footage, testimonial, blog draft). If DMS needs to generate the piece, add another 30 to 60 seconds. The system is designed for a team member who has a coffee-break window, not a full studio session." },
  { q: "Does this replace my agency or in-house designer?", a: "No. It replaces the strategic vacuum that sits above your creative team. Give your designer a clear brief and a channel; give your instructor-turned-social-manager a hook and a CTA. The Operator does not shoot footage or design carousels \u2014 it decides what to shoot, why, and where it fits." },
  { q: "How is this different from a generic AI content tool?", a: "Generic AI tools start with a blank prompt box. The Strategy Operator starts with your business. Every output is built around your offers, audience, channels, brand voice and constraints \u2014 not internet averages." },
  { q: "Do I need marketing experience?", a: "No. The onboarding walks you through structured questions in plain language. If you can describe your courses, your ideal customer, and how you currently reach them, the Operator handles the strategy." },
  { q: "Is the calendar editable?", a: "Yes. Tasks can be marked done, skipped, or opened for editing. The plan is a working document, not a printed brochure." },
  { q: "Does DMS post for me?", a: "No. DMS produces the outputs. You publish them. Human judgement, human timing, human context." },
  { q: "Can we use it across multiple locations or brands?", a: "Yes. Pro Unlimited supports multiple workspaces, each with its own Dive Brain, its own 90-day strategy, and its own team access." },
];

export default function ProTools() {
  useEffect(() => {
    document.title = "Pro Unlimited \u00b7 90-Day Strategy Operator \u00b7 Dive Media Strategist";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "A strategic operating system for dive businesses. Build your next 90 days of marketing in minutes. One task per day. \u20ac99.90/month.");
    setMeta("og:title", "The 90-Day Strategy Operator \u00b7 built for dive businesses");
    setMeta("og:description", "Positioning, pillars, channel roles and a rolling 90-day marketing calendar. Owner sets it up once. The team ticks one task a day.");
  }, []);

  return (
    <div data-testid="pro-tools-page" className="min-h-screen bg-[#F8FAFC] text-[#1F2937]">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F8FAFC]/85 border-b border-[#E2E8F0]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 shrink-0" data-testid="pro-tools-logo">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
              <span className="text-[#4FD1C5] font-heading font-bold text-xs sm:text-sm">D</span>
            </div>
            <div className="leading-none">
              <div className="font-heading font-bold text-[13px] sm:text-[15px] text-[#0A2342]">Dive Media Strategist&trade;</div>
              <div className="hidden sm:block text-[10px] tracking-[0.22em] text-[#64748B] uppercase mt-1">For dive businesses</div>
            </div>
          </Link>
          <nav className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Link to="/login" data-testid="pro-tools-nav-login" className="hidden sm:inline-flex dms-btn-ghost">Sign in</Link>
            <Link to="/register?intent=pro_unlimited" data-testid="pro-tools-nav-register" className="dms-btn-primary text-[13px] sm:text-[14px] px-3 sm:px-5 py-2 sm:py-2.5">
              Build my Dive Brain <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E2E8F0]/60">
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute -top-40 -left-20 w-[520px] h-[520px] rounded-full bg-[#4FD1C5]/8 blur-3xl" />
          <div className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-[#1D4E89]/6 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="dms-badge mb-6" data-testid="pro-tools-hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4FD1C5]" />
              Pro Unlimited &middot; &euro;99.90 / month
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-heading font-bold leading-[1.02] tracking-tight text-[#0A2342]">
              A strategic operating system<br />
              <span className="text-[#1D4E89]">for dive businesses.</span>
            </h1>
            <p className="mt-6 text-[17px] sm:text-[19px] leading-[1.55] text-[#334155] max-w-2xl">
              The 90-Day Strategy Operator turns your offers, audience and channels into a rolling marketing calendar. One clear task per day. Hook, angle, CTA and prep notes included. Built for owners and their teams — not for influencers, not for hobbyists.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/register?intent=pro_unlimited" data-testid="pro-tools-hero-cta-primary" className="dms-btn-primary w-full sm:w-auto justify-center px-6 py-3.5 text-[15px]">
                Build my Dive Brain <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="pro-tools-hero-cta-secondary" className="dms-btn-ghost w-full sm:w-auto justify-center px-6 py-3.5 text-[15px]">
                Sign in
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 text-[12.5px] sm:text-[13.5px] text-[#475569] max-w-xl">
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#4FD1C5] shrink-0" /> 90-day rolling plan</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#4FD1C5] shrink-0" /> Multi-workspace</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#4FD1C5] shrink-0" /> GDPR-ready</div>
            </div>
          </div>

          {/* Editorial product mock */}
          <div className="lg:col-span-5 lg:pl-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[28px] bg-[#4FD1C5]/10 blur-2xl" />
              <div className="relative dms-card p-5 sm:p-6 border-2 border-[#0A2342]/8">
                <div className="flex items-center justify-between">
                  <div className="dms-label">Today &middot; Day 34 / 90</div>
                  <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Week 5</div>
                </div>
                <div className="mt-4 rounded-md bg-[#0A2342] text-[#F8FAFC] px-4 py-3">
                  <div className="text-[10.5px] tracking-[0.22em] uppercase text-[#4FD1C5] font-bold">Instagram &middot; Reel</div>
                  <div className="mt-1 font-heading text-[17px] leading-snug">
                    &ldquo;If you&apos;re nervous underwater, you&apos;re breathing wrong.&rdquo;
                  </div>
                </div>
                <dl className="mt-4 text-[13px] leading-relaxed">
                  <div className="flex gap-3 py-1.5 border-b border-[#E2E8F0]">
                    <dt className="text-[#64748B] w-16 shrink-0 uppercase tracking-[0.12em] text-[10.5px] font-bold pt-0.5">Angle</dt>
                    <dd className="text-[#1F2937]">Address the root cause of dive anxiety through breathwork awareness — not more training dives.</dd>
                  </div>
                  <div className="flex gap-3 py-1.5 border-b border-[#E2E8F0]">
                    <dt className="text-[#64748B] w-16 shrink-0 uppercase tracking-[0.12em] text-[10.5px] font-bold pt-0.5">CTA</dt>
                    <dd className="text-[#1F2937]">DM &ldquo;BREATHE&rdquo; for a free breathwork guide.</dd>
                  </div>
                  <div className="flex gap-3 py-1.5 border-b border-[#E2E8F0]">
                    <dt className="text-[#64748B] w-16 shrink-0 uppercase tracking-[0.12em] text-[10.5px] font-bold pt-0.5">Prep</dt>
                    <dd className="text-[#1F2937]">Use student testimonial footage if available. Keep tone calm and expert.</dd>
                  </div>
                  <div className="flex gap-3 py-1.5">
                    <dt className="text-[#64748B] w-16 shrink-0 uppercase tracking-[0.12em] text-[10.5px] font-bold pt-0.5">Pillar</dt>
                    <dd className="text-[#1F2937]">Nervous-to-calm &middot; Nervous beginner FAQ</dd>
                  </div>
                </dl>
                <div className="mt-4 grid grid-cols-3 gap-2 text-[12px] font-semibold">
                  <div className="text-center rounded-md bg-[#4FD1C5]/20 text-[#0A2342] py-2">Mark done</div>
                  <div className="text-center rounded-md bg-[#F1F5F9] text-[#64748B] py-2">Skip</div>
                  <div className="text-center rounded-md bg-[#0A2342] text-white py-2">Generate</div>
                </div>
              </div>
              <div className="mt-3 text-[11.5px] text-[#94A3B8] text-right">A real task, generated by the Operator.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="border-b border-[#E2E8F0]/60">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="dms-label mb-4">The problem</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-[44px] text-[#0A2342] leading-[1.1] tracking-tight max-w-3xl">
            Most dive businesses don&apos;t have a content problem.<br />
            <span className="text-[#1D4E89]">They have a strategy problem.</span>
          </h2>
          <div className="mt-8 text-[16px] leading-[1.65] text-[#334155] max-w-2xl space-y-4">
            <p>They post when they remember. They promote courses when things go quiet. They copy whatever reel format is trending this week.</p>
            <p>Instagram, Facebook, email, WhatsApp, Google — all running, none of it connected.</p>
            <p className="font-semibold text-[#0A2342]">More content ideas won&apos;t fix that. A plan will.</p>
          </div>
        </div>
      </section>

      {/* Team workflow */}
      <section className="border-b border-[#E2E8F0]/60 bg-[#0A2342] text-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-4">Designed for teams</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-[42px] leading-[1.1] tracking-tight">
              One person sets the strategy.<br />
              <span className="text-[#4FD1C5]">Anyone on the team executes it.</span>
            </h2>
            <p className="mt-6 text-[16px] leading-[1.65] text-[#CBD5E1] max-w-md">
              The Operator is built for how dive businesses actually run: the owner or marketing lead completes onboarding once, generates and approves the 90-day strategy, and hands the daily calendar to the team.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            {[
              { icon: Sparkles, title: "One-time setup", body: "15 to 25 minutes with the person who knows the offers, goals and season.", tag: "OWNER / MANAGER" },
              { icon: Calendar, title: "Rolling 90-day plan", body: "Positioning, pillars, campaign arcs, weekly themes and one daily task per posting day.", tag: "OWNER / MANAGER" },
              { icon: ClipboardCheck, title: "5 to 15 minutes per day", body: "Open DMS, read the task, film or type, mark done. Or click Generate for the full asset.", tag: "TEAM" },
              { icon: LineChart, title: "The plan compounds", body: "Awareness \u2192 objection breaking \u2192 social proof \u2192 conversion \u2192 nurture, across three strategic months.", tag: "TEAM" },
            ].map((item) => (
              <div key={item.title} className="rounded-lg border border-white/8 bg-white/4 backdrop-blur-sm p-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-md bg-[#4FD1C5]/15 text-[#4FD1C5] flex items-center justify-center">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5]">{item.tag}</div>
                </div>
                <div className="mt-4 font-heading font-bold text-[17px] text-white">{item.title}</div>
                <div className="mt-2 text-[13.5px] text-[#94A3B8] leading-relaxed">{item.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="border-b border-[#E2E8F0]/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="dms-label mb-4">The five-step workflow</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] leading-[1.1] tracking-tight">
              From onboarding to a working calendar in a single session.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {WORKFLOW.map((s) => (
              <div key={s.n} data-testid={`pro-tools-step-${s.n}`} className="dms-card p-6 flex flex-col">
                <div className="font-heading font-bold text-[#4FD1C5] text-[13px] tracking-[0.18em]">{s.n}</div>
                <div className="mt-3 font-heading font-bold text-[16px] text-[#0A2342] leading-snug">{s.title}</div>
                <div className="mt-3 text-[13.5px] text-[#475569] leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-b border-[#E2E8F0]/60 bg-white/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="dms-label mb-4">What you get</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] leading-[1.1] tracking-tight">
              Strategy before content. Channel-specific. Offer-aware. Brand-safe.
            </h2>
            <p className="mt-6 text-[16px] text-[#475569] leading-[1.65] max-w-2xl">
              A dive centre with one person doing marketing can&apos;t operate like a media team — and the Operator doesn&apos;t pretend it can. It builds around your real posting capacity, your real assets, and your real season.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="dms-card p-6">
                <div className="w-10 h-10 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center">
                  <o.icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-heading font-bold text-[17px] text-[#0A2342] leading-snug">{o.title}</div>
                <div className="mt-2.5 text-[13.5px] text-[#475569] leading-relaxed">{o.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="border-b border-[#E2E8F0]/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="dms-label mb-4">Free vs Pro Unlimited</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] leading-[1.1] tracking-tight">
              Free users create content.<br />
              <span className="text-[#1D4E89]">Pro Unlimited users get the strategy telling them what content to create.</span>
            </h2>
            <p className="mt-4 text-[15.5px] text-[#64748B]">Not &quot;pay more for more tools&quot;. Pay more to stop guessing.</p>
          </div>
          <div className="mt-10 overflow-hidden rounded-lg border border-[#E2E8F0]" data-testid="pro-tools-comparison">
            <table className="w-full text-left">
              <thead className="bg-[#F1F5F9]">
                <tr className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#64748B]">
                  <th className="py-4 px-5 sm:px-6 w-[36%]">Feature</th>
                  <th className="py-4 px-5 sm:px-6 w-[28%]">Free &amp; Creator</th>
                  <th className="py-4 px-5 sm:px-6 w-[36%] bg-[#0A2342] text-[#4FD1C5]">Pro Unlimited</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([label, free, unl], i) => (
                  <tr key={label} className={i % 2 ? "bg-white" : "bg-[#F8FAFC]"}>
                    <td className="py-3.5 px-5 sm:px-6 font-semibold text-[13.5px] text-[#0A2342]">{label}</td>
                    <td className="py-3.5 px-5 sm:px-6 text-[13.5px] text-[#64748B]">{free}</td>
                    <td className="py-3.5 px-5 sm:px-6 text-[13.5px] text-[#0A2342] font-medium">{unl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Founder statement */}
      <section className="border-b border-[#E2E8F0]/60 bg-[#F1F5F9]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-5">Why this exists</div>
          <blockquote className="font-heading text-[26px] sm:text-[32px] leading-[1.25] tracking-tight text-[#0A2342] max-w-3xl">
            &ldquo;The tools I wish had existed when I ran marketing for dive businesses. Built by divers, for the diving industry. Not another generic AI content generator dressed in ocean colours.&rdquo;
          </blockquote>
          <div className="mt-8 text-[12.5px] tracking-[0.14em] uppercase font-bold text-[#64748B]">— Dive Media Strategist team</div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-b border-[#E2E8F0]/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-2xl">
            <div className="dms-label mb-4">Built for</div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] leading-[1.1] tracking-tight">Serious dive businesses that treat marketing as an operating function.</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {VERTICALS.map((v) => (
              <div key={v.label} className="dms-card p-5 flex flex-col items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-[#4FD1C5]/15 text-[#0A2342] flex items-center justify-center">
                  <v.icon className="w-4 h-4" />
                </div>
                <div className="text-[13px] font-semibold text-[#0A2342] leading-snug">{v.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-[#E2E8F0]/60 bg-white/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="dms-label mb-4">One plan. Everything included.</div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] leading-[1.1] tracking-tight">
                Pro Unlimited.<br />
                <span className="text-[#1D4E89]">&euro;99.90 per month.</span>
              </h2>
              <p className="mt-5 text-[15.5px] text-[#475569] leading-[1.6] max-w-md">
                Everything the Operator produces belongs to you. Cancel any time. 7-day free trial with the full Dive Brain, unlocked so you can build the strategy before you commit.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/register?intent=pro_unlimited" data-testid="pro-tools-pricing-cta" className="dms-btn-primary justify-center px-6 py-3.5 text-[15px]">
                  Start 7-day free trial <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/consulting" data-testid="pro-tools-pricing-consulting" className="dms-btn-ghost justify-center px-6 py-3.5 text-[15px]">
                  Talk to us
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="dms-card p-7 sm:p-8 border-2 border-[#4FD1C5]/40 relative">
                <div className="absolute -top-3 left-7 dms-badge bg-[#4FD1C5] text-[#0A2342]">
                  <Sparkles className="w-3 h-3" /> Included on Pro Unlimited
                </div>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {[
                    "The 90-Day Strategy Operator",
                    "Unlimited Strategy Credits",
                    "Multi-workspace support",
                    "Every content tool in DMS",
                    "Dive Brain per workspace",
                    "Daily email &amp; WhatsApp reminders",
                    "Consistency reports every week",
                    "PDF, CSV and .ics calendar exports",
                    "Priority customer support",
                    "GDPR-ready data handling",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[14px] text-[#1F2937]">
                      <div className="w-4 h-4 rounded-full bg-[#4FD1C5] text-white flex items-center justify-center shrink-0 mt-0.5">
                        <ChevronRight className="w-3 h-3" />
                      </div>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-[#E2E8F0] text-[12.5px] text-[#64748B]">
                  Sign up now &middot; complete a short business onboarding &middot; approve your strategy &middot; the calendar starts the same day.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[#E2E8F0]/60">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="dms-label mb-4">Common questions</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#0A2342] leading-[1.1] tracking-tight">
            What owners ask before they start.
          </h2>
          <div className="mt-10 divide-y divide-[#E2E8F0] border-t border-b border-[#E2E8F0]">
            {FAQS.map((f, i) => (
              <details key={f.q} className="group py-5" data-testid={`pro-tools-faq-${i}`}>
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <span className="font-heading font-bold text-[16.5px] text-[#0A2342] leading-snug">{f.q}</span>
                  <ChevronRight className="w-5 h-5 shrink-0 mt-0.5 text-[#64748B] transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-[14.5px] text-[#475569] leading-[1.7] max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#0A2342] text-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10 py-20 sm:py-28 text-center">
          <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-6">Stop posting randomly</div>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
            Build the plan.<br />
            <span className="text-[#4FD1C5]">Follow the calendar.</span>
          </h2>
          <p className="mt-6 text-[16px] sm:text-[17px] text-[#CBD5E1] max-w-xl mx-auto leading-[1.6]">
            Generate the content when you need it. Operate marketing like the rest of the business.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register?intent=pro_unlimited" data-testid="pro-tools-closing-cta" className="dms-btn-primary justify-center px-7 py-3.5 text-[15px]">
              Build my Dive Brain <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="dms-btn-ghost justify-center px-7 py-3.5 text-[15px] !text-[#F8FAFC] !border-white/20 hover:!bg-white/10">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2342] text-[#94A3B8] border-t border-white/8">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[12.5px]">
          <div>&copy; {new Date().getFullYear()} Dive Media Strategist. Built for the diving industry.</div>
          <div className="flex items-center gap-5">
            <Link to="/legal/terms" className="hover:text-white">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/trust" className="hover:text-white">Trust</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
